// world.ts — city layout shared by spawning, item motion and rendering.
//
// The map is a 6×6 grid of city blocks separated by straight roads. Every
// spawn rule below is derived from this single geometry, so props, vehicles
// and pedestrians can never end up on top of each other or inside a road:
//
//   road lines      x or z ∈ {-60,-30,0,30,60}, half width 4
//   block centres   x or z ∈ {-75,-45,-15,15,45,75}, half size 10.25
//   block interior  ±7.2 around a block centre (static props/buildings)
//   vehicle lanes   1.5 (cars) / 3.7 (bikes) from a road centre
//   sidewalk lanes  5.2 / 5.9 / 6.6 from a road centre (walkers)
//
// Keeping the movable lanes outside the block interior means a walking
// pedestrian can never clip through a parked car or a building.

export type Motion = "none" | "walk" | "road" | "fly";

export const WORLD_HALF = 78;
export const ROAD_SPACING = 30;
export const ROAD_HALF = 4;
export const ROAD_LINES = [-60, -30, 0, 30, 60] as const;
export const BLOCK_CENTERS = [-75, -45, -15, 15, 45, 75] as const;
export const BLOCK_HALF = 10.25;
/** Static props stay inside this square around the block centre. */
export const BLOCK_INNER = 7.2;
/** Extra breathing room added to every footprint-pair distance. */
export const MIN_ITEM_GAP = 0.5;

export interface Spot {
  x: number;
  z: number;
}

export interface Lane extends Spot {
  /** Axis the item travels along. */
  axis: "x" | "z";
  /** World coordinate of the road centre line (the value that stays fixed). */
  line: number;
  /** +1 / -1 along the travel axis. */
  dir: 1 | -1;
  vx: number;
  vz: number;
}

/** Distance from `value` to the nearest road centre line. */
export function roadDistance(value: number): number {
  return Math.abs(value - Math.round(value / ROAD_SPACING) * ROAD_SPACING);
}

/** True when a circle of `radius` at (x, z) touches a road surface. */
export function isOnRoad(x: number, z: number, radius = 0): boolean {
  return roadDistance(x) < ROAD_HALF + radius || roadDistance(z) < ROAD_HALF + radius;
}

/** True when the circle sits fully inside a block interior (props only). */
export function isInBlockInterior(x: number, z: number, radius: number): boolean {
  if (isOnRoad(x, z, radius)) return false;
  for (const bx of BLOCK_CENTERS) {
    for (const bz of BLOCK_CENTERS) {
      if (
        Math.abs(x - bx) + radius <= BLOCK_INNER &&
        Math.abs(z - bz) + radius <= BLOCK_INNER
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Random point inside a random block, leaving room for `radius`. */
export function blockSpot(random: () => number, radius: number): Spot {
  const bx = BLOCK_CENTERS[Math.floor(random() * BLOCK_CENTERS.length)] ?? 0;
  const bz = BLOCK_CENTERS[Math.floor(random() * BLOCK_CENTERS.length)] ?? 0;
  const room = Math.max(0, BLOCK_INNER - radius);
  return { x: bx + (random() * 2 - 1) * room, z: bz + (random() * 2 - 1) * room };
}

/** Lateral offset from the road centre line for a moving kind. */
export function laneOffset(motion: Motion, id: string): number {
  if (motion === "road") {
    if (id === "cyclist") return 3.7;
    return 1.5; // cars: single lane per direction, same speed per kind
  }
  if (motion === "walk") {
    if (id === "pedestrian") return 5.0;
    if (id === "dog") return 5.9;
    return 6.8; // cats keep closer to the walls
  }
  return 0;
}

/**
 * Pick a lane for a moving item. Cars and bikes share the road with their own
 * kind only (all members of a kind use the same speed), so their spacing can
 * never collapse into an overlap.
 */
export function laneSpot(random: () => number, motion: Motion, id: string, speed: number): Lane {
  const axis: "x" | "z" = random() < 0.5 ? "x" : "z";
  const line = ROAD_LINES[Math.floor(random() * ROAD_LINES.length)] ?? 0;
  const dir: 1 | -1 = random() < 0.5 ? 1 : -1;
  const offset = laneOffset(motion, id) * (dir === 1 ? -1 : 1);
  // Start anywhere along the lane; wrap-around keeps it circulating.
  const along = (random() * 2 - 1) * (WORLD_HALF - 2);
  if (axis === "x") {
    return { x: along, z: line + offset, axis, line, dir, vx: speed * dir, vz: 0 };
  }
  return { x: line + offset, z: along, axis, line, dir, vx: 0, vz: speed * dir };
}

/** Advance a lane-bound item by dt, wrapping around the map edge. */
export function advanceLane(x: number, z: number, vx: number, vz: number, dt: number): Spot {
  const limit = WORLD_HALF;
  let nx = x + vx * dt;
  let nz = z + vz * dt;
  if (nx > limit) nx -= limit * 2;
  else if (nx < -limit) nx += limit * 2;
  if (nz > limit) nz -= limit * 2;
  else if (nz < -limit) nz += limit * 2;
  return { x: nx, z: nz };
}

/** Yaw for a model whose local forward axis is +x. */
export function yawFor(vx: number, vz: number): number {
  if (vx === 0 && vz === 0) return 0;
  return Math.atan2(-vz, vx);
}

/** True when two circles keep the required minimum distance. */
export function clearOf(
  spot: Spot,
  radius: number,
  others: ReadonlyArray<{ x: number; z: number; radius: number }>,
  extraGap = MIN_ITEM_GAP,
): boolean {
  for (const other of others) {
    const need = radius + other.radius + extraGap;
    const dx = spot.x - other.x;
    const dz = spot.z - other.z;
    if (dx * dx + dz * dz < need * need) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// City plan: every block gets a role and a tidy layout. Buildings stand in
// the middle of their plot, street furniture lines the plot border, parked
// cars stand in rows. The same rules exist in Go (server/internal/room/
// world.go) so the arena city looks like the solo city.

export type BlockRole = "downtown" | "office" | "residential" | "park" | "commercial" | "parking";

export interface Placement {
  /** Catalog id, e.g. "town_house". */
  id: string;
  x: number;
  z: number;
}

/** Radius lookup for catalog ids (client footprints are radii). */
export type RadiusOf = (id: string) => number;

const EDGE_PATTERN = ["bench", "trash_can", "small_tree", "bench", "small_tree", "parcel", "traffic_cone"] as const;

export function blockRole(bx: number, bz: number, random: () => number): BlockRole {
  const ring = Math.max(Math.abs(bx), Math.abs(bz));
  const roll = random();
  if (ring <= 15) return roll < 0.5 ? "downtown" : "office";
  if (ring <= 45) return roll < 0.35 ? "office" : roll < 0.5 ? "commercial" : roll < 0.85 ? "residential" : "park";
  return roll < 0.5 ? "residential" : roll < 0.68 ? "park" : roll < 0.83 ? "parking" : roll < 0.93 ? "office" : "commercial";
}

/** Layout for one block in block-local coordinates (centre = 0,0). */
export function planBlock(role: BlockRole, random: () => number, radiusOf: RadiusOf): Placement[] {
  const out: Placement[] = [];
  const taken: Array<{ x: number; z: number; radius: number }> = [];
  const put = (id: string, x: number, z: number) => {
    const radius = radiusOf(id);
    if (Math.abs(x) + radius > BLOCK_INNER + 1e-6 || Math.abs(z) + radius > BLOCK_INNER + 1e-6) return false;
    if (!clearOf({ x, z }, radius, taken)) return false;
    out.push({ id, x, z });
    taken.push({ x, z, radius });
    return true;
  };
  const edges: Array<"n" | "s" | "e" | "w"> = [];

  switch (role) {
    case "downtown":
      if (random() < 0.55) {
        put("city_block", 0, 0);
      } else {
        put("office_tower", 0, 0);
        edges.push("n", "s", "e", "w");
      }
      break;
    case "office":
      put("office_tower", 0, 0);
      edges.push("n", "s", "e", "w");
      break;
    case "residential": {
      // Two houses side by side, gardens/benches along the street sides.
      const along = random() < 0.5;
      if (along) { put("town_house", -3.75, 0); put("town_house", 3.75, 0); edges.push("n", "s"); }
      else { put("town_house", 0, -3.75); put("town_house", 0, 3.75); edges.push("e", "w"); }
      break;
    }
    case "park": {
      put("fountain", 0, 0);
      const trees = 6;
      const offset = random() * Math.PI;
      for (let i = 0; i < trees; i++) {
        const a = offset + (i / trees) * Math.PI * 2;
        put("small_tree", Math.cos(a) * 3.7, Math.sin(a) * 3.7);
      }
      if (random() < 0.6) put("food_cart", 0, 5.6);
      edges.push("n", "s", "e", "w");
      break;
    }
    case "commercial":
      put("food_kiosk", -3.4, -1.2);
      put("food_kiosk", 3.4, 1.2);
      put("food_cart", 3.6, -3.6);
      put("food_cart", -3.6, 3.6);
      edges.push("n", "s", "e", "w");
      break;
    case "parking": {
      for (const z of [-3.2, 3.2]) {
        if (random() < 0.3) {
          put("city_bus", -3.4, z);
          put(random() < 0.5 ? "compact_car" : "delivery_van", 3.6, z);
        } else {
          for (const x of [-4.7, 0, 4.7]) {
            const r = random();
            if (r < 0.15) continue; // free bay
            put(r < 0.7 ? "compact_car" : "delivery_van", x, z);
          }
        }
      }
      edges.push("n", "s");
      break;
    }
  }

  // Street furniture snug against the plot border.
  let k = Math.floor(random() * EDGE_PATTERN.length);
  for (const edge of edges) {
    for (const t of [-6.1, -3.05, 0, 3.05, 6.1]) {
      const id = t > 6 ? "street_lamp" : EDGE_PATTERN[k++ % EDGE_PATTERN.length]!;
      if (random() < 0.15) continue; // a few gaps look less machine-made
      const inset = BLOCK_INNER - radiusOf(id) - 0.02;
      const [x, z] = edge === "n" ? [t, inset] : edge === "s" ? [t, -inset] : edge === "e" ? [inset, t] : [-inset, t];
      put(id, x, z);
    }
  }

  // A little clutter in whatever space is left.
  for (let i = 0; i < 2; i++) {
    const id = random() < 0.5 ? "parcel" : "traffic_cone";
    put(id, (random() * 2 - 1) * 6, (random() * 2 - 1) * 6);
  }
  return out;
}

/** Full static city: every block planned, in world coordinates. */
export function planCity(random: () => number, radiusOf: RadiusOf): Placement[] {
  const out: Placement[] = [];
  for (const bx of BLOCK_CENTERS) {
    for (const bz of BLOCK_CENTERS) {
      const role = blockRole(bx, bz, random);
      for (const p of planBlock(role, random, radiusOf)) out.push({ id: p.id, x: bx + p.x, z: bz + p.z });
    }
  }
  return out;
}

function nearestBlockCentre(v: number): number {
  let best: number = BLOCK_CENTERS[0];
  for (const c of BLOCK_CENTERS) if (Math.abs(v - c) < Math.abs(v - best)) best = c;
  return best;
}

/**
 * Deterministic facing for a static prop from its position alone, so the
 * arena (which only sends x/z) renders the same orientation as solo.
 * Models face local +z (house fronts, bench seats) or drive along +x (cars).
 */
export function layoutYaw(id: string, x: number, z: number): number {
  const lx = x - nearestBlockCentre(x);
  const lz = z - nearestBlockCentre(z);
  const sideways = Math.abs(lx) > Math.abs(lz);
  switch (id) {
    case "compact_car":
    case "delivery_van":
    case "city_bus":
      return lx < 0 ? 0 : Math.PI;
    case "bench":
      // Seat faces into the plot, backrest to the street.
      return layoutYaw("town_house", x, z) + Math.PI;
    case "food_cart":
    case "town_house":
    case "food_kiosk":
    case "office_tower": {
      // Front (+z) faces the nearest street.
      const dx = sideways ? Math.sign(lx) || 1 : 0;
      const dz = sideways ? 0 : Math.sign(lz) || 1;
      return Math.atan2(dx, dz);
    }
    case "city_block":
    case "fountain":
      return 0;
    default: {
      const h = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
      return (h - Math.floor(h)) * Math.PI * 2;
    }
  }
}
